import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import {
  SITE_NAME,
  SERVICE_AREA,
  DEFAULT_TITLE,
  DEFAULT_DESCRIPTION,
  DEFAULT_IMAGE,
  absoluteUrl
} from '../../seo/site';

// Per-page <head> for the customer storefront.
//
// The canonical URL is the CURRENT path by default. It used to default to the
// site root for every page that didn't pass one - and none did - so every
// store page told search engines it was a duplicate of the homepage (on the
// old domain, too). Query strings are deliberately dropped: the store page's
// ?category=&product= deep links are the same page as /store/:id.
//
// `noindex` keeps a page out of search results while still letting crawlers
// follow its links. `jsonLd` takes one schema.org object or an array of them.
const SEO = ({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  canonicalPath,
  image = DEFAULT_IMAGE,
  noindex = false,
  jsonLd
}) => {
  const { pathname } = useLocation();
  const url = absoluteUrl(canonicalPath || pathname);
  const schemas = [].concat(jsonLd || []).filter(Boolean);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, follow" />}

      {/* Open Graph - what WhatsApp, Facebook and friends show for a shared link */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_IN" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />

      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Every storefront page is about the same service area */}
      <meta name="geo.region" content={SERVICE_AREA.regionCode} />
      <meta name="geo.placename" content={`${SERVICE_AREA.place}, ${SERVICE_AREA.region}`} />

      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
